import type { LiveVehicle, TrackerHealth } from './types';

/**
 * TRACKER HEALTH — the pure model behind slice 13d. It renders the two facts the
 * UI used to swallow into `unknown` (Q19): whether the tracker is in contact and
 * reporting, and its power state — kept apart from vehicle state (`motion`) and
 * from each other (backup battery vs vehicle supply).
 *
 * Everything here is PURE — no i18n, no clock, no React — so the rules that decide
 * WHICH facts a surface may honestly state are unit-tested without a browser. The
 * components in `TrackerHealth.tsx` render what these functions return and invent
 * nothing.
 */

/** A finite number, or null. Guards the wire: NaN/Infinity/non-number → null, so a
 *  garbage reading can never render as `0` or a placeholder implying a real value. */
function numOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** A non-empty trimmed string, or null. */
function strOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** A real boolean, or null (slice 56b). Anything non-boolean → null, so a garbage
 *  reading never renders as an on/off power state we do not hold. */
function boolOrNull(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

/**
 * Normalise the wire's `trackerHealth` (seam S1). PRESERVES the null-vs-present
 * distinction that is the whole design: an absent/non-object payload → `null` (no
 * telemetry); an object → an object with each field independently softened to null
 * (present, possibly with no readings). It never fabricates the object from a null,
 * and never fabricates a field from an absent one.
 */
export function normalizeTrackerHealth(value: unknown): TrackerHealth | null {
  if (value == null || typeof value !== 'object') return null;
  const o = value as Record<string, unknown>;
  return {
    batteryVolts: numOrNull(o.batteryVolts),
    externalVoltage: numOrNull(o.externalVoltage),
    charge: boolOrNull(o.charge),
    batteryLevelPct: numOrNull(o.batteryLevelPct),
    rssi: numOrNull(o.rssi),
    sats: numOrNull(o.sats),
    reportedAt: strOrNull(o.reportedAt),
  };
}

/**
 * Is the tracker in contact? This is the tracker-liveness question, and the server
 * already answered it in `status` (against `OFFLINE_AFTER_SEC`, api view.ts) — the
 * same source `freshness.ts` consumes. We never re-derive a threshold on the client.
 * `unclear` is the honest verdict when the server itself could not tell (`unknown`).
 */
export type TrackerContactState = 'inContact' | 'noContact' | 'unclear';

export function trackerContactState(v: LiveVehicle): TrackerContactState {
  if (v.status === 'online') return 'inContact';
  if (v.status === 'offline') return 'noContact';
  return 'unclear';
}

/**
 * NO BATTERY BAND / LEVEL — deliberately. The backup reading is a raw CELL VOLTAGE
 * (~3.8 V Li-ion), not a percentage. The old `batteryLevel` bands (≤15 critical,
 * ≤40 low) were percentage guesses that rendered a healthy 3.798 V cell as
 * "4% — critical". Voltage→health thresholds for this cell would be a NEW unmeasured
 * guess, and CLAUDE.md forbids shipping a threshold we cannot point to a measurement
 * for — a wrong "critical" is exactly the harm being fixed. So the voltage is
 * rendered plainly, in the normal reading colour, with no level and no warning
 * state. A level can return later, derived from observation.
 *
 * `externalVoltage`'s "no supply" state below is DIFFERENT and stays: those are
 * measured readings (12.9 V resting, 13.7 V charging), and near-zero genuinely means
 * the supply is cut.
 */

/**
 * Vehicle-supply state, from `externalVoltage`. This DESCRIBES the reading; it is
 * not an alert and raises nothing (the power-cut alert is a separate, future slice —
 * absence over disabled). ~0 V is reported honestly as "no supply" because that is
 * what the number says, the same way battery renders "low"; it is a label on a
 * measured value, not a claim the UI invented.
 */
export type PowerState = 'supplied' | 'noSupply';

/** Below this the external supply reads as cut/absent. Above ~0 it is present. */
export const NO_SUPPLY_BELOW_VOLTS = 1;

export function powerState(volts: number): PowerState {
  return volts < NO_SUPPLY_BELOW_VOLTS ? 'noSupply' : 'supplied';
}

/**
 * The rendering verdict for a surface — what it may honestly say. Collapses the
 * null-vs-present distinction into an explicit shape so a component cannot get it
 * wrong: `present` false is "no telemetry"; `present` true with `noReadings` is
 * "reporting, but this report carried no sensor values".
 */
export interface TrackerHealthView {
  /** A `trackerHealth` payload exists (the tracker reported telemetry). */
  present: boolean;
  /** Payload exists but every reading field is null — reporting, no readings. */
  noReadings: boolean;
  contact: TrackerContactState;
  batteryVolts: number | null;
  externalVoltage: number | null;
  /** slice 56b — connected-to-vehicle-power boolean; the gt06 fallback when
   *  `externalVoltage` is null. Null when unreported. */
  charge: boolean | null;
  /** slice 56b — backup battery as a PERCENT (VL110C); never a voltage. Null when
   *  unreported. */
  batteryLevelPct: number | null;
  rssi: number | null;
  sats: number | null;
  /**
   * OUR pipeline's processing stamp (the wire's `reportedAt` = raad-api's
   * `telemetryAt` = `cache.ts`'s `cachedAt`), NOT a device report time. The poller
   * rewrites it every 5 s whether or not the device said anything, so it reads
   * ≈now straight through a total silence. It is kept here only to preserve the
   * wire contract and its normalize test — it MUST NEVER be rendered as a
   * report/freshness time on any surface. The tracker "last report" line reads
   * `LiveVehicle.trackerContactAt` (Traccar `lastupdate`, a real message from the
   * device) instead. (slice 13d)
   */
  reportedAt: string | null;
}

export function trackerHealthView(v: LiveVehicle): TrackerHealthView {
  const contact = trackerContactState(v);
  const h = v.trackerHealth;
  if (h == null) {
    return {
      present: false,
      noReadings: false,
      contact,
      batteryVolts: null,
      externalVoltage: null,
      charge: null,
      batteryLevelPct: null,
      rssi: null,
      sats: null,
      reportedAt: null,
    };
  }
  // A VL110C that reports only `charge`/`batteryLevelPct` (no volts) IS reporting
  // readings — count them, so it is never mislabelled "reporting, no readings".
  const noReadings =
    h.batteryVolts == null &&
    h.externalVoltage == null &&
    h.charge == null &&
    h.batteryLevelPct == null &&
    h.rssi == null &&
    h.sats == null;
  return {
    present: true,
    noReadings,
    contact,
    batteryVolts: h.batteryVolts,
    externalVoltage: h.externalVoltage,
    charge: h.charge,
    batteryLevelPct: h.batteryLevelPct,
    rssi: h.rssi,
    sats: h.sats,
    reportedAt: h.reportedAt,
  };
}

/** True when there is anything worth rendering in a tracker-health block — either a
 *  telemetry payload, or a definite contact verdict from the server. When false the
 *  surface shows a single "no telemetry" note rather than an empty box. */
export function hasTrackerHealth(view: TrackerHealthView): boolean {
  return view.present || view.contact !== 'unclear';
}

/**
 * ONE guidance line for the `unknown` vehicle state — the state that needs
 * explaining (slice 13d). A customer looking at this at 2 a.m. should not need a
 * message sent weeks ago: `unknown` alone says nothing about WHY, and the three
 * reasons want three different responses. This picks which single line to show; it
 * is null for every non-`unknown` state (nothing to explain there).
 *
 * PURE — it decides only WHICH case applies; the i18n copy lives in the component.
 * Contact is NOT re-derived here: `trackerContactState` reads the server's `status`
 * verdict (the same source `freshness.ts` trusts). The supply threshold is the
 * existing `powerState` / `NO_SUPPLY_BELOW_VOLTS` — no new number is invented.
 *
 * WORDING IS A HARD CONSTRAINT (Mahad): the `noSupply` case says the tracker lost
 * VEHICLE POWER and stops there — never "theft", "stolen" or "tampering". A loose
 * battery terminal produces this exact reading; the customer draws their own
 * conclusion. The component copy honours this.
 *
 *   - `noSupply`      — a HELD external reading below the supply threshold: the
 *                       tracker has lost vehicle power. Highest priority, because it
 *                       is the most actionable and true whether or not it is still
 *                       in contact (it may run on backup battery).
 *   - `noContact`     — the tracker has stopped reporting (server verdict `offline`).
 *   - `inContactNoSky`— in contact and NOT out of supply: reporting normally, just
 *                       somewhere with no clear view of the sky; position is
 *                       last-known. This case makes NO claim about power, so it is
 *                       honest whether `externalVoltage` is present or null (the
 *                       real RAAD-TEST-01 state — a null reading we do not hold).
 *   - `null`          — contact unclear (server `unknown`): assert nothing.
 */
export type UnknownStateGuidance = 'noSupply' | 'noContact' | 'inContactNoSky' | null;

export function unknownStateGuidance(v: LiveVehicle): UnknownStateGuidance {
  if (v.motion !== 'unknown') return null;
  const ext = v.trackerHealth?.externalVoltage ?? null;
  // A HELD low reading means the supply is cut — surface it first. A null reading is
  // NOT low: we do not know the supply, so this branch never fires on null.
  if (ext != null && powerState(ext) === 'noSupply') return 'noSupply';
  const contact = trackerContactState(v);
  if (contact === 'noContact') return 'noContact';
  if (contact === 'inContact') return 'inContactNoSky';
  // Contact unclear (server `unknown`): the honest answer is to say nothing.
  return null;
}
